<template>
    <div>
        <div class="bg-white">
            <div class="container">
                <div class="py-3" v-if="!loading">
                    <router-link to="/"
                                 class="d-flex align-items-center text-decoration-none">
                        <i class="fas fa-arrow-left text-dark pr-2"></i>
                        <span class="text-dark font-weight-bold">Back to Home</span>
                    </router-link>
                </div>
                <div v-else class="py-3">
                    <div class="d-flex align-items-center text-decoration-none">
                        <i class="fas fa-times text-dark pr-2"></i>
                        <span class="text-dark font-weight-bold">Wait for process to complete. Do not use your browsers back button!</span>
                    </div>
                </div>
                <div v-if="machine">
                    <div class="d-flex justify-content-lg-start justify-content-end">
                        <h4>{{ machine.Title }}</h4>
                    </div>
                    <div class="d-flex flex-column flex-lg-row justify-content-lg-start justify-content-end pb-3">
                        <span class="text-muted flex-sm-row">Stock: {{ machine.StockNumber }}</span>
                        <span class="text-muted flex-sm-row pl-lg-3">
                            Photos:
                            <span v-html="photoCount"></span>
                        </span>
                        <span class="text-muted flex-sm-row pl-lg-3">
                            Last Updated: {{ dateModified(machine.DateModified) }}
                            {{ modifiedBy }}
                        </span>
                        <span v-if="unsavedPhotoCount"
                              class="flex-sm-row pl-lg-3 font-weight-bold text-danger">{{ unsavedPhotoCount }} unsaved image(s).</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="container drop-container">
            <div class="d-flex mt-3 py-3 box-shadow bg-white" :class="{'fixed-bottom mx-3': isMobile }">
                <div class="text-uppercase font-weight-bold flex-column">

                    <button type="button"
                            class="btn btn-link btn-no-hover-effect d-none d-lg-inline-flex d-md-none text-decoration-none text-dark font-weight-bold text-uppercase"
                            v-on:click="selectAll()">
                        Select All Images
                    </button>
                    <button @click="bulkDownload()"
                            v-if="this.selected.length > 0"
                            type="button"
                            class="btn btn-link btn-no-hover-effect d-none d-lg-inline-flex text-decoration-none text-dark font-weight-bold text-uppercase">
                        Download Selected Images
                    </button>
                    <button @click="deleteMultipleImages()"
                            v-if="this.selected.length > 0"
                            type="button"
                            class="btn btn-link btn-no-hover-effect d-none d-lg-inline-flex text-decoration-none text-dark font-weight-bold text-uppercase">
                        Delete Selected Images
                    </button>
                </div>
                <div class="text-uppercase font-weight-bold align-items-center mr-lg-2 ml-lg-auto">
                    <file-upload class="btn btn-primary titan-red d-inline-flex  mx-2 align-items-center text-decoration-none font-weight-bold text-uppercase add-file"
                                 :multiple="true"
                                 :drop="true"
                                 :drop-directory="true"
                                 v-model="photos"
                                 @input-filter="inputFilter"
                                 ref="upload">
                        <i class="fas fa-plus mr-1"></i>Add Photos
                    </file-upload>
                    <!--
                        Home-page upload flow: hide Save/Wait until a machine
                        has been selected. Before machine selection there is
                        no target for a save — the embedded Search component
                        is active and the user has nothing to save against.
                        Showing Save + the unsaved-changes banner at that
                        point implies an action that isn't available yet.
                    -->
                    <button v-if="machine && !loading" type="button"
                            class="btn btn-primary border-0 mb-0  text-decoration-none font-weight-bold ml-auto text-uppercase titan-red"
                            :disabled="!hasPendingChanges || isProcessingImages"
                            @click="uploadImages">
                        {{ isProcessingImages ? 'Processing images…' : 'Save' }}
                    </button>
                    <button v-else-if="machine" type="button" class="btn btn-primary border-0 mb-0  text-decoration-none font-weight-bold ml-auto text-uppercase titan-gray">
                        Wait
                    </button>
                </div>
            </div>

            <Search v-if="!machine"
                    class="mt-3"
                    @updateSelectedMachine="updateSelectedMachine($event)" />
            <!--
                Home-page upload flow: suppress the unsaved-changes banner
                until a machine has been selected. The banner's "click save
                or your changes will be lost" message is misleading when the
                Save button itself is hidden at this step — there's no save
                action to take yet.
            -->
            <div class="change-notification mt-3 p-3 font-weight-bold text-uppercase text-center" v-if="machine && (unsavedChanges || unsavedPhotoCount > 0)">
                <span>
                    You have unsaved changes! You must click save or your changes will be lost.
                </span>

            </div>

            <div class="card-columns">
                <draggable :list="imagesForCards"
                           handle=".drag-handle"
                           :setData="handleSetData"
                           :delay="0"
                           :delayOnTouchOnly="true"
                           :touchStartThreshold="3"
                           :animation="150"
                           :move="updateSortOrder"
                           @change="onDragChange"
                           :disabled="draggableDisabled "
                           :forceFallback="true"
                           fallbackClass="drag-fallback-ghost"
                           :fallbackTolerance="3">
                    <!--
                        Bug #58 fix: use file.id (a stable per-image GUID) as the key
                        instead of the array index. When keyed on index, Vue reuses the
                        same ImageCard (and its embedded PhotoEditor + cropper instance)
                        in each DOM slot across reorders and save responses, leaving the
                        cropper's internal src out of sync with the image prop. The
                        result is Edit sometimes opening a stale image. Keying on id
                        causes Vue to move DOM nodes with the data and correctly remount
                        editors when the underlying image actually changes.
                    -->
                    <div class="machine-card"
                         v-for="(file, index) in imagesForCards"
                         :key="(file && file.id) || ('new-' + index)">

                        <transition :duration="{ enter: 500, leave: 800 }">
                            <ImageCard v-if="file && file.IsArchived != true" ref="imagecards"
                                       :image="file"
                                       :machine="machine"
                                       :unsavedHold="unsavedChanges"
                                       :loading="isBusy"
                                       :index="index"  
                                       :bulkSelect="bulkSelect"
                                       @updateSelectedImage="addToSelectedImages($event)"
                                       @allItemsSelected ="addToSelectedImages($event)"
                                       @deleteSelectedImage="deleteImage($event)"
                                       @disableDraggableCards="disableDrag($event)"
                                       @edit-save-start="onEditSaveStart"
                                       @edit-save-end="onEditSaveEnd" />
                        </transition>
                    </div>
                </draggable>
            </div>

            <div v-if="archiveActive">
                <div v-if="archivedImages && archivedImages.length">
                    <hr />
                    <h5 v-if="archivedImages && archivedImages.length">
                        Archived Images
                    </h5>
                </div>

                <div class="card-columns">
                    <!-- Bug #58 fix: key on file.id, not index. See matching comment above. -->
                    <div class="machine-card"
                         v-for="(file, index) in archivedImages"
                         :key="file.id || ('arch-' + index)">
                        <ImageCard :image="file"
                                   :machine="machine"
                                   :unsavedHold="unsavedChanges"
                                   :loading="isBusy"
                                   :index="index"
                                   :bulkSelect="bulkSelect"
                                   :isArchived="true"
                                   @restoreArchivedImage="restoreImage($event)" />
                    </div>
                </div>
                <div v-if="displayNoPhotos">
                    <h3 class="text-center my-3">No Photos</h3>
                </div>
            </div>
        </div>
        <div v-if="showModal" class="notes-modal" tabindex="-1">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Web Notes</h5>
                        
                    </div>
                    <div class="modal-body">
                        <p>{{webnotes}}</p>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="showModalDialog" data-bs-dismiss="modal">Close</button>
                    </div>
                </div>
            </div>
        </div>

    </div>
    
</template>

<script>
    import ImageCard from '@/components/ImageCard';
    import Search from '@/components/Search';
    import draggable from 'vuedraggable';
    import FileUpload from 'vue-upload-component';
    import UploaderMixins from '../mixins/UploaderMixins.js';
    import EquipmentDataService from '../services/EquipmentDataService';
    import axios from "axios";


    export default {
        name: 'PhotoUploader',
        mixins: [UploaderMixins],
        components: {
            Search,
            FileUpload,
            draggable,
            ImageCard,

        },
        props: {
            id: {
                type: String,
                required: false,
            },
            files: {
                type: Array,
                required: false,
            },
            passedMachine: {
                type: Object,
                required: false,
            },
        },
        data() {
            return {
                // Home-page upload flow fix: when PhotoUploader is loaded with
                // files already present in route params (from PhotoSelector's
                // Next button), the photos watcher does not fire for the initial
                // value — it only fires on subsequent changes. Without seeding
                // unsavedPhotoCount here, the computed hasPendingChanges stays
                // false and the Save button stays disabled even though a photo
                // is clearly in the grid ready to save.
                //
                // When loaded from UploadHistory (no `files` prop), this falls
                // through to 0 — identical to the previous `null` behavior for
                // the `null > 0` check that drives hasPendingChanges.
                unsavedPhotoCount: (this.files && this.files.length) || 0,
                photos: this.files,
                webnotes: "",
                windowWidth: window.innerWidth,
                bulkSelect: false,
                unsavedChanges: false,
                showModal: false,
                selected: [],
                results: [],
                draggableDisabled: false,
                imagesForCards: [],
                machine: this.passedMachine,
                archivedImages: [],
                // maxSize is provided by UploaderMixins (sourced from the
                // maxImageMB constant at the top of UploaderMixins.js). Don't
                // redeclare it here — a component-level maxSize would shadow
                // the mixin's, causing the two definitions to drift over
                // time. Update the limit in UploaderMixins.js.
                allowedExtensions: ['jpg', 'jpeg', 'png','heic'],
                authorizedArchiveImageAccounts: [
                    'Imageuploaderadmin@titanmachinery.com'
                ],
                loading: false,
                render: true,
            };
        },
        created() {

            // Non-reactive reference tracker used by the machine watcher to
            // distinguish "machine.ImageData got reassigned" from "a nested
            // item property was mutated." The leading underscore keeps it out
            // of Vue's reactivity system so setting it never triggers watchers.
            this._lastMachineImageDataRef = null;

            Object.entries(localStorage).forEach(([key]) => {
                if (!key.includes('adal')) delete localStorage[key];
            });

            // Home-page upload flow: path is /edit with no stock number (slug
            // is empty) while the user is still picking a machine via the
            // embedded Search component. In that state we must NOT fire the
            // getEquipmentById / getArchivedImagesByMachine calls below,
            // because:
            //
            //   * The backend's eqData endpoint falls through to the recent-
            //     activity list query when StockNumber is empty — returning
            //     up to 200 records instead of a single record.
            //   * getMachineData() then blindly does
            //         this.machine = response.Data[0];
            //     which assigns the most-recently-modified machine to this
            //     page, dismissing the Search widget (v-if="!machine") and
            //     making it look like the user "landed" on some random
            //     machine's detail page a few seconds after clicking Next.
            //   * The inline getEquipmentById then throws a TypeError because
            //     list-mode responses don't populate per-record ImageData,
            //     so response.Data[0].ImageData is undefined.
            //
            // Skipping these calls when slug is empty leaves the machine null
            // so Search stays visible until the user actually picks a
            // machine; once they do, updateSelectedMachine assigns this.machine
            // and the machine watcher loads imagesForCards from the provided
            // record.
            let slug = this.$route.path.substring(6);

            if (!this.passedMachine && this.$route.path.includes('edit') && slug) {
                this.getMachineData();
            }

            if (this.$route.path.includes('edit') && slug) {
                EquipmentDataService.getEquipmentById(slug).then((response) => {
                    if (response.Data[0].ImageData) {
                        this.imagesForCards = response.Data[0].ImageData.filter((imgData) => imgData.IsArchived === false);

                    }
                    this.webnotes = response.Data[0].WebsiteNotes;
                });
                EquipmentDataService.getArchivedImagesByMachine(slug).then((response) => {
                    this.archivedImages = response[0].ImageData;
                    this.webnotes = response[0].WebsiteNotes;
                });
            }

        },
        mounted() {
            
            this.getImagesForCards();
           
            window.addEventListener('resize', () => {
                this.windowWidth = window.innerWidth;
            });
            
        },
        computed: {
            // True whenever there is at least one thing the Save button could
            // actually persist: a pending resort, a pending in-place edit, or
            // one or more newly added photos not yet uploaded. Drives the
            // Save button's :disabled. Prevents the "click Save on an
            // unchanged record, spinner runs forever" bug — uploadImages()
            // only resets isBusy inside the mixin save callbacks, and with
            // nothing to save neither callback is reached.
            hasPendingChanges() {
                return this.unsavedChanges || (this.unsavedPhotoCount > 0);
            },
            // True while any newly added image is still being compressed/read
            // and its ImageFile payload isn't ready yet. Gates the Save button
            // so a user can't submit before the data exists (see the silent-
            // drop fix — pendingCompressions lives in UploaderMixins).
            isProcessingImages() {
                return this.pendingCompressions > 0;
            },
            displayNoPhotos() {
                return Boolean(
                    !this.photos && (!this.machine || !this.machine.ImageCount)
                );
            },
            photoCount() {
                var nonArchived = 0;
                if (this.machine.ImageData) {
                    nonArchived = this.machine.ImageData.filter((k) => k.IsArchived == false);
                }
                return nonArchived.length;
            },
            modifiedBy() {
                return this.machine.ModifiedBy
                    ? ' by ' + this.machine.ModifiedBy
                    : ' by System';
            },
            archiveActive() {
                //var profileEmail = 'derrick.nssn@gmail.com';
                return Boolean(false
                    //this.authorizedArchiveImageAccounts.includes(profileEmail)
                );
            },
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.handleResize);
        },
        methods: {
            getMachineData() {
                let slug = this.$route.path.substring(6);
                EquipmentDataService.getEquipmentById(slug).then((response) => {
                    this.machine = response.Data[0];
                });
            },
            showModalDialog() {

                this.showModal = !this.showModal
                console.log(this.showModal)
            },
            convertShortcodes(text) {
                const regex = /\[text:(.+?)\s+link:(.+?)\]/g;

                return text.replace(regex, (match, linkText, url) => {
                    return `<a href="${url.trim()}">${linkText.trim()}</a>`;
                });
            },
            restoreFromArchive(event) {
                this.restoreImage(event);
                
            },
            handleSetData(dataTransfer) {
                // Set an empty image as the drag image to remove the default ghost
                dataTransfer.setDragImage(new Image(), 0, 0);
            },
            getImagesForCards() {
                // Image-triplication bug fix: this used to push machine.ImageData
                // items onto imagesForCards (which created() had already populated)
                // and then sometimes overwrite imagesForCards with this.photos.
                // That pattern could leave stale or duplicated entries in the grid.
                // Rebuild the array from scratch on every call so there is one
                // canonical source of truth per run: server-backed images first,
                // then any local photos not already present by id.
                var next = [];
                if (this.machine && this.machine.ImageData) {
                    this.machine.ImageData.forEach((img) => {
                        if (!img.IsArchived) {
                            next.push(img);
                        }
                    });
                }
                if (this.photos && this.photos.length) {
                    this.photos.forEach((p) => {
                        if (!this.isInArray(p, next)) {
                            next.push(p);
                        }
                    });
                }
                this.imagesForCards = next;
            },
            selectAll() {
                // Sync each card's visual checkbox state and the parent's
                // `selected` array in one shot, rather than going through
                // ic.selectImage() (which now toggles isChecked locally and
                // would fight the assignment we just made above).
                //
                // Bug fix: previously this routed deselect through
                // removeFromSelectedImages, which wiped the entire `selected`
                // array on the first iteration; combined with the visual/data
                // desync on individual checkbox clicks, users could see
                // checkboxes appear unchecked while `selected` still held
                // every image, causing "Delete Selected" to delete everything.
                this.bulkSelect = !this.bulkSelect;
                if (this.$refs.imagecards) {
                    this.$refs.imagecards.forEach((ic) => {
                        ic.isChecked = this.bulkSelect;
                    });
                }
                if (this.bulkSelect) {
                    this.selected = (this.imagesForCards || [])
                        .filter((img) => img && !img.IsArchived)
                        .slice();
                } else {
                    this.selected = [];
                }
            },
            bulkDownload() {

                this.selected.forEach((image) => {
                    this.downloadSelected(image);

                })
            },
            downloadSelected(image) {
                axios
                    .get(image.BlobImageUrl, {
                        responseType: "blob",
                    }).then((response) => {
                        if (response) {
                            var FileSaver = require("file-saver");
                            FileSaver.saveAs(
                                new Blob([response.data]),
                                image.StockNumber + ".jpg"
                            );
                        }
                    });
            },
            updateSortOrder(evt) {
                // Pure validator for vuedraggable's :move callback. This fires
                // on EVERY drag-over event during a single drag (many per second
                // as the cursor moves), so it must not mutate state — any
                // accumulation across drag-over events corrupts the data model.
                //
                // Previously this method mutated Status, Resorted, and SortOrder
                // on both the dragged item AND whichever item the cursor happened
                // to be over at the moment. Across many drags, items that were
                // merely hovered got incorrectly flagged as "Resorted", and
                // repeated SortOrder swaps on partially-settled items drifted
                // the data out of sync with the DOM — the user saw drag stop
                // working reliably after several moves.
                //
                // The actual "this item moved" mutation now lives in onDragChange
                // (wired to @change), which fires exactly once per completed
                // drop and identifies the moved item precisely.
                //
                // Here we only validate: bail cleanly when either side isn't
                // a real item (hovering over empty space, mid-transition DOM,
                // etc.). Returning false cancels this particular hover-move.
                const dragged = evt && evt.draggedContext && evt.draggedContext.element;
                const related = evt && evt.relatedContext && evt.relatedContext.element;
                if (!dragged || !related) {
                    return false;
                }
                return true;
            },
            onDragChange(evt) {
                // Fires once per completed drop. evt.moved = { element, oldIndex, newIndex }.
                //
                // A drag shifts every item in the closed interval
                // [min(oldIndex, newIndex), max(oldIndex, newIndex)]. Dragging
                // position 2 → position 0 means the dragged item plus the two
                // items that got pushed down all moved; dragging 0 → 2 means
                // the dragged item plus the two items that shifted up all
                // moved. Everything outside that range stayed put.
                //
                // We deliberately do NOT touch image.SortOrder here. The
                // imagesForCards deep watcher runs on every array change and
                // normalizes image.SortOrder = (array index + 1) for all
                // items. Writing SortOrder here in addition appears to cause
                // the machine deep watcher to fire in an order that rebuilds
                // imagesForCards from machine.ImageData (which is still in
                // the pre-drag order) — producing a visual "snap back" on
                // drop. Writing only Status/Resorted matches the mutation
                // surface of the previous revision that reordered cleanly.
                //
                // We use oldIndex/newIndex rather than comparing SortOrder
                // values because the imagesForCards watcher has already
                // normalized SortOrder by the time @change fires, so a
                // SortOrder-based "did this item shift?" check always
                // resolves to false and never flags anything. The indices
                // are captured at drag time and are the reliable signal.
                if (!evt || !evt.moved) {
                    return;
                }
                const oldIndex = evt.moved.oldIndex;
                const newIndex = evt.moved.newIndex;
                if (typeof oldIndex !== 'number' || typeof newIndex !== 'number') {
                    return;
                }
                const start = Math.min(oldIndex, newIndex);
                const end = Math.max(oldIndex, newIndex);
                if (this.imagesForCards) {
                    for (let i = start; i <= end; i++) {
                        const image = this.imagesForCards[i];
                        if (!image) {
                            continue;
                        }
                        image.Status = 'U';
                        image.Resorted = true;
                    }
                }
                this.unsavedChanges = true;
            },
            uploadImages() {
                if (this.isBusy) return;

                // Gate on confirm BEFORE setting isBusy, so Cancel doesn't lock out the next Save click.
                if (!this.machine || !confirm(`Apply changes to ${this.machine.Title}?`)) {
                    if (this.machine) {
                        alert('Please remember to click save to preserve your changes.');
                    }
                    return;
                }

                this.isBusy = true;
                var sortedImages = [];
                var newImages = [];

                this.imagesForCards.forEach((image) => {
                    if (image.Resorted && image.Resorted == true && !image.IsNewFile) {
                        sortedImages.push(image);
                    } else if (image.IsUploaded == false && image.IsNewFile == true) {
                        newImages.push(image);
                    }
                });

                // Stuck-spinner fix: if nothing actually needs saving we skip
                // the two mixin calls, which means their .finally() would never
                // fire and isBusy would stay true forever, spinning every card.
                // The Save button is also :disabled when !hasPendingChanges, so
                // this branch is normally unreachable — keeping it as a safety
                // net in case an edge case (timing, watcher order, programmatic
                // trigger) bypasses the disabled state.
                if (sortedImages.length === 0 && newImages.length === 0) {
                    this.isBusy = false;
                    return;
                }

                // Silent-drop fix (hard backstop): a new image's ImageFile is
                // populated asynchronously by inputFilter's compression, so a
                // fast Save can fire before the payload exists. An empty
                // ImageFile is then silently discarded server-side (the record
                // never reaches Cosmos) while the client still shows
                // "Image Saved!". The Save button is disabled while
                // isProcessingImages, but that counter lives per-component and
                // won't cover images whose compression started in
                // PhotoSelector before routing here — so re-check the actual
                // objects at click time and refuse to send any new image whose
                // ImageFile isn't ready yet.
                if (this.pendingCompressions > 0 || newImages.some((image) => !image.ImageFile)) {
                    this.isBusy = false;
                    alert('Some images are still processing. Please wait a moment and try again.');
                    return;
                }

                if (sortedImages.length > 0 && newImages.length > 0) {
                    // Both a sort change AND a new-image upload are pending.
                    // The two backend endpoints each perform read-modify-write
                    // on the same Cosmos record. Firing them in parallel races
                    // the writes: the new-image endpoint typically reads the
                    // pre-sort Cosmos state and then writes back its
                    // (sort-less) version on top of the sort save's commit.
                    //
                    // Result on the user's screen: Cosmos ends up reverted to
                    // the original order, but the sort save has already
                    // physically renamed blob slots. Cosmos record A@1 then
                    // points to a blob slot that now holds a different
                    // image's bytes, producing the "duplicate image appears,
                    // original is gone" symptom QA reported when uploading
                    // new images and reordering in the same session.
                    //
                    // Chaining serializes them: existingSave commits its
                    // Cosmos write, then newSave reads the post-sort state
                    // and appends on top of it. Pass silent=true to
                    // existingSave so only one toast appears at chain end.
                    const p = this.uploadExistingImageData(sortedImages, true);
                    if (p && typeof p.then === 'function') {
                        p.then(() => this.uploadNewImageData(newImages));
                    } else {
                        // Defensive fallback: if the mixin method didn't
                        // return a promise (unexpected), run both in
                        // parallel rather than dropping the new-image save.
                        this.uploadNewImageData(newImages);
                    }
                } else if (sortedImages.length > 0) {
                    this.uploadExistingImageData(sortedImages);
                } else if (newImages.length > 0) {
                    this.uploadNewImageData(newImages);
                }
                // isBusy is reset inside the mixin methods' .finally() callbacks.
            },
            updateSelectedMachine(val) {
                this.machine = val;
            },
            addToSelectedImages(val) {
                // Toggle by id (not reference) — newly uploaded photos can be
                // re-wrapped after save, so reference equality isn't reliable
                // across the lifecycle.
                const idx = this.selected.findIndex(
                    (item) => item && val && item.id === val.id
                );
                if (idx === -1) {
                    this.selected.push(val);
                } else {
                    this.selected.splice(idx, 1);
                }
            },
            deleteImage(val) {
                if (confirm('Are you sure that you want to delete this image?')) {
                    if (this.isBusy) { return; }
                    this.isBusy = true;
                    this.deleteSelected(val);
                    this.removeImagesFromArray(val);
                    let slug = this.$route.path.substring(6);
                    let that = this;
                    EquipmentDataService.getArchivedImagesByMachine(slug).then(
                        (response) => {
                            that.archivedImages = response[0].ImageData;
                            that.$nextTick(() => {
                                that.isBusy = false;
                                that.unsavedChanges = false;
                            });
                        }
                    );
                    this.ImageCount = this.ImageCount--;                    
                }
            },
            deleteMultipleImages() {
                if (this.isBusy) return;
                if (!this.selected.length) return;
                // Confirm BEFORE any removal from imagesForCards, so Cancel keeps the grid intact.
                if (!confirm("Are you sure that you want to delete the images?")) return;

                this.isBusy = true;
                var imagesToDelete = [];

                this.selected.forEach((image) => {
                    imagesToDelete.push(image);
                    this.removeImagesFromArray(image);
                });
                this.deleteSelectedImages(imagesToDelete);
                this.isBusy = false;

            },
            removeImagesFromArray(image) {
                const index = this.imagesForCards.indexOf(image);
                if (index > -1) {
                    this.imagesForCards.splice(index, 1);
                }
            },

            isInArray(value, array) {
                var result = false;
                array.forEach(function (arr) {
                    if (arr.id === value.id) {
                        result = true;
                    }
                });
                return result;  
            },
            disableDrag(val) {
                this.draggableDisabled = val;
            },
            // Bug #57 fix: when a PhotoEditor save kicks off (via the event
            // chain from PhotoEditor -> ImageCard -> here), toggle the main
            // page's busy flags so the Save button switches to "Wait" and
            // the user gets visual feedback that a save is in progress.
            onEditSaveStart() {
                this.isBusy = true;
                this.loading = true;
            },
            onEditSaveEnd() {
                this.isBusy = false;
                this.loading = false;
            },
        },
        watch: {
            photos: {
                handler: function () {
                    if (this.photos) {
                        this.unsavedPhotoCount = this.photos.length;
                    }
                    this.photos.forEach((element) => {
                        if (this.imagesForCards) {
                            var checkArrayDups = this.isInArray(element, this.imagesForCards);

                            if (!checkArrayDups) {
                                this.imagesForCards.push(element);
                            }
                        }
                    });

                },
            },
            machine: {
                // Bug #56 fix: without deep: true this watcher only fires when
                // `this.machine` itself is reassigned. The save flow mutates
                // this.machine.ImageData (a nested property) to install the
                // server response — which Vue 2 cannot detect without a deep
                // watch. The net effect was that after saving a newly added
                // image, imagesForCards kept referencing the original upload
                // objects with local .blob URLs, leaving isBlob=true on every
                // card and the Download link disabled until a manual refresh.
                deep: true,
                handler: function () {
                    // Deliberately no longer sets unsavedChanges here.
                    //
                    // This watcher fires on ANY nested mutation of machine or
                    // machine.ImageData. During the bulk-delete flow, the mixin
                    // mutates image.Status, image.IsDeleted, etc. on items
                    // still in machine.ImageData — each of those mutations
                    // fired this watcher, and the old `if (this.files.length > 0)
                    // this.unsavedChanges = true` block flipped the flag back
                    // on right in the middle of a save operation. The user then
                    // saw the "YOU HAVE UNSAVED CHANGES" banner linger after a
                    // bulk delete even though the server had already persisted
                    // the deletion and there was literally nothing left to
                    // save. unsavedChanges is set by explicit user-intent
                    // events (drag reorder → onDragChange, in-place edit →
                    // edit-save-start), not by deep watchers.
                    if (this.machine && this.machine.ImageData) {
                        // Rebuild imagesForCards ONLY when machine.ImageData
                        // is a different array reference than last time we
                        // rebuilt. This distinguishes two causes of this
                        // watcher firing:
                        //
                        //   a) machine.ImageData was reassigned — e.g. after
                        //      a save (`this.machine.ImageData = serverData`).
                        //      We want to rebuild: items in the UI now come
                        //      from the server and should replace whatever
                        //      was showing.
                        //
                        //   b) A nested property on an item within
                        //      machine.ImageData was mutated — e.g. during
                        //      drag we set `item.Status = 'U'` and
                        //      `item.Resorted = true`. With deep: true,
                        //      this also fires the watcher, but the array
                        //      reference is the same. Rebuilding here would
                        //      wipe the user's drag order (because the
                        //      rebuild happens from machine.ImageData, which
                        //      was NOT reordered by the drag — vuedraggable
                        //      only mutated imagesForCards). That was the
                        //      drag-snap-back bug observed after the
                        //      home-page upload flow, because saving a new
                        //      photo is what first populates
                        //      machine.ImageData with real shared references
                        //      to imagesForCards items.
                        //
                        // The UploadHistory flow doesn't hit (b) because it
                        // never populates machine.ImageData — imagesForCards
                        // is set directly from getEquipmentById response and
                        // its items aren't shared with machine.ImageData.
                        // Reference tracking makes both flows behave the
                        // same way.
                        if (this._lastMachineImageDataRef !== this.machine.ImageData) {
                            this._lastMachineImageDataRef = this.machine.ImageData;

                            // Filter archived items so they don't leak into the
                            // active grid when the watcher re-syncs after a save.
                            this.imagesForCards = this.machine.ImageData
                                .filter((img) => img.IsArchived === false);

                            // Home-page upload flow: preserve any pending local
                            // photos that haven't been saved yet. When the user
                            // picks a machine, this watcher fires with a fresh
                            // machine.ImageData reference (new), so we're in the
                            // rebuild branch here. Without this block, the
                            // pending photo would be wiped because machine.ImageData
                            // only contains server records at that moment.
                            //
                            // isInArray is id-based. Local photos have vue-upload-
                            // component-assigned ids; server images have distinct
                            // Cosmos ids. So the existence check here will never
                            // confuse one for the other.
                            //
                            // Safe vs. the old triplication bug: uploadNewImageData
                            // sets `this.photos = []` BEFORE any mutations to
                            // machine.ImageData, so during save this iteration walks
                            // an empty array and never re-pushes saved items.
                            if (this.photos && this.photos.length) {
                                this.photos.forEach((p) => {
                                    if (!this.isInArray(p, this.imagesForCards)) {
                                        this.imagesForCards.push(p);
                                    }
                                });
                            }
                        }
                    }

                    // Image-triplication bug fix: do NOT re-push photos here.
                    // With deep: true this watcher fires on every nested mutation
                    // to machine.ImageData. The save flow in uploadNewImageData
                    // does 3+ mutations in a row (empty out, assign server
                    // response, push each archived item). If photos is iterated
                    // inside this watcher, every iteration can re-push the same
                    // local photo object into imagesForCards — because
                    // uploadNewImageData set item.id=null before sending, which
                    // means isInArray (id-based) never matches the server items.
                    // Photos syncing is the photos watcher's job, not the machine
                    // watcher's.
                },
            },
            imagesForCards: {
                handler: function () {
                   
                    if (this.imagesForCards) {
                        this.imagesForCards.forEach((image, i) => {
                            // Skip transient undefined/null entries that can appear
                            // mid-drag when vuedraggable's move is cancelled or when
                            // a new photo is being spliced in. Without this guard
                            // the whole forEach crashes with "Cannot set properties
                            // of undefined (setting 'SortOrder')".
                            if (!image) {
                                return;
                            }
                            // Keep SortOrder aligned with the array position so it
                            // reflects the grid order the user sees.
                            image.SortOrder = i + 1;

                            // IMPORTANT: don't set Resorted here.
                            //
                            // Previously this block flagged items as Resorted whenever
                            // SortOrder !== PrevSortOrder. That fires spuriously
                            // whenever imagesForCards rebuilds from server data with
                            // gaps in SortOrder — most commonly when any archived
                            // images get filtered out, leaving the remaining items
                            // with PrevSortOrder values (e.g. 1,2,3,5) that don't
                            // match their new indices (1,2,3,4). Those items then
                            // show up as "Resorted" and the save flow pushes them
                            // into sortedImages even when the user didn't drag
                            // anything. Combined with a newly added image, that
                            // produced two parallel save calls (uploadExistingImageData
                            // and uploadNewImageData), which is why users saw two
                            // "Image Saved!" toasts after a single save.
                            //
                            // Resorted is only a real user-intent signal when set by
                            // updateSortOrder in response to an actual drag move.
                            // Leave it alone here.
                        });
                    }
                },
            },
        },
    };
</script>
<style scoped>
    .drop-container{
        height: 100vh;
    }
    .notes-modal {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: block;
        z-index: 1;
        min-width: 80%;
    }
</style>